import { IpcRendererEvent } from 'electron'
import { AudioInputSettings, AudioOutputSettings, AudioSourceInfo, AudioTrackWithInfo } from '../../UserContext'
import { ipc } from '../../services/ElectronIPC'
import FakeMediaStreamTrack, { FakeMediaStreamTrackConstructorParams } from './FakeMediaStreamTrack'
import { TypedEmitter } from 'tiny-typed-emitter'
import { AudioEngineDevSettings } from '../../AppContext'
import { logger } from '../../services/Logger'
import { env } from '../../services/EnvironmentVariables'
import {
    AudioEngineError,
    AudioEngineAudioDeviceInfo,
    AudioEngineOutputDeviceType,
    AudioEngineNetworkStats,
    AudioEngineStreamStatsEvent,
    AudioEngineAudioStreamInfo,
    AudioEngineConnectionState,
} from './types'
import { IAudioEngine, AudioEngineEventEmitter, AudioEngineImpl } from './IAudioEngine'
import { AudioEngineRoom } from './AudioEngineRoom'

const log = logger.createChildLogger({ name: 'AudioEngine' })

function audioOutputSettingsCompatibility(source: AudioSourceInfo | null, settings: AudioOutputSettings) {
    if (settings.compressionBitrate == null) {
        settings.compressionBitrate = 0
    }
    if (settings.isSelective == null) {
        settings.isSelective = false
    }
    if (settings.includeStreams == null) {
        settings.includeStreams = []
    }
    if (settings.excludeStreams == null) {
        settings.excludeStreams = []
    }

    if (settings.channelCount == null || settings.channelCount <= 0) {
        if (source?.maxOutputChannels == null) {
            log.error(
                'setAudioOutputDevice',
                `settings.channelCount is null or 0! source.maxOutputChannels ` +
                    `is also null -- setting channelCount to 1.`,
                {
                    source,
                    settings,
                }
            )

            settings.channelCount = 1
        } else {
            if (source.maxOutputChannels <= 2) {
                log.error(
                    'setAudioOutputDevice',
                    `settings.channelCount is null or 0! Setting channelCount to source.maxOutputChannels==` +
                        `${source.maxOutputChannels}.`,
                    {
                        source,
                        settings,
                    }
                )
                settings.channelCount = source.maxOutputChannels
            } else {
                log.error(
                    'setAudioOutputDevice',
                    `settings.channelCount is null or 0! Setting channelCount to 2 ` +
                        `because ${source.maxOutputChannels} > 2.`,
                    {
                        source,
                        settings,
                    }
                )
                settings.channelCount = 2
            }
        }
    }

    return settings
}

export class AudioEngine extends TypedEmitter<AudioEngineEventEmitter> implements IAudioEngine {
    // These are tracks for tx
    private openTracks: Map<string, AudioTrackWithInfo> = new Map()

    constructor() {
        super()

        ipc.on('AudioEngine.onInitializationSuccess', (_: IpcRendererEvent, _result: any) => {
            this.emit('onInitializationSuccess')
        })

        ipc.on('AudioEngine.onFatalError', (_: IpcRendererEvent, result: AudioEngineError) => {
            this.emit('onFatalError', result)
        })

        ipc.on('AudioEngine.streamStatsEvent', (_: IpcRendererEvent, result: AudioEngineStreamStatsEvent) => {
            Object.keys(result.localStreamStats).forEach((streamUuid) => {
                const stats = result.localStreamStats[streamUuid] || null
                const track = this.openTracks.get(streamUuid) || null
                if (stats && track instanceof FakeMediaStreamTrack) {
                    track.internalSetCurrentLocalStats(stats)
                }
            })
        })

        ipc.on(
            'AudioEngine.onNetworkStatusChanged',
            (_: IpcRendererEvent, result: { eventName: AudioEngineConnectionState }) => {
                this.emit('onNetworkStatusChanged', result.eventName)
            }
        )

        ipc.on('AudioEngine.networkStatsEvent', (_: IpcRendererEvent, stats: AudioEngineNetworkStats) => {
            this.emit('networkStatsEvent', stats)
        })

        ipc.on('AudioEngine.onStreamEnded', (_: IpcRendererEvent, data: any) => {
            const streamUuid = data?.streamUuid || ''
            const track = this.openTracks.get(streamUuid)
            if (track instanceof FakeMediaStreamTrack) {
                const handlers = track.evListeners.get('ended') || []
                handlers.forEach((h) => {
                    h()
                })
            }
        })
    }

    async init() {}

    createRoom(): AudioEngineRoom {
        return new AudioEngineRoom()
    }

    getCurrentRoom(): AudioEngineRoom | null {
        return new AudioEngineRoom()
    }

    async setSettings(settings: AudioEngineDevSettings, authToken?: string): Promise<boolean> {
        return new Promise((resolve) => {
            ipc.send('AudioEngine.setSettings', { ...settings, authToken })
            ipc.once(
                'AudioEngine.setSettings',
                (
                    _ev: IpcRendererEvent,
                    args: { result: AudioEngineAudioDeviceInfo[] } | { error: AudioEngineError }
                ) => {
                    if ('error' in args) {
                        resolve(false)
                    } else {
                        resolve(true)
                    }
                }
            )
        })
    }

    getAudioDevicesTimer: NodeJS.Timeout | undefined = undefined
    async getAudioDevices(): Promise<AudioEngineAudioDeviceInfo[] | null> {
        return new Promise((resolve) => {
            // timeout
            const timeoutMs = 8000
            clearTimeout(this.getAudioDevicesTimer)
            this.getAudioDevicesTimer = setTimeout(() => {
                log.error('getAudioDevices', `call timed out in ${timeoutMs}.`)
                resolve([])
            }, timeoutMs)

            if (!env.TEST_AUDIO_ENGINE_GET_AUDIO_DEVICES_TIMEOUT) {
                ipc.send('AudioEngine.getAudioDevices', {})
            }
            ipc.once(
                'AudioEngine.getAudioDevices',
                (
                    _ev: IpcRendererEvent,
                    args: { result: AudioEngineAudioDeviceInfo[] } | { error: AudioEngineError }
                ) => {
                    clearTimeout(this.getAudioDevicesTimer)
                    if ('error' in args) {
                        log.error('getAudioDevices', 'call returned error.', { error: args.error })
                        resolve(null)
                        return
                    }

                    // TEMPORARY -- FOR TESTING
                    // args.result.forEach(a => {
                    //     if (a.name.indexOf('CABLE-A Output') >= 0) {
                    //         a.maxInputChannels = 8
                    //     }
                    // })

                    resolve(args.result)
                }
            )
        })
    }

    async checkSupportedSampleRates(params: {
        audioEngineDeviceId: string
        sampleRates: number[]
        deviceDirection: 'input' | 'output'
    }): Promise<number[]> {
        return new Promise((resolve, _reject) => {
            ipc.send('AudioEngine.checkSupportedSampleRates', params)
            ipc.once('AudioEngine.checkSupportedSampleRates', (_ev: IpcRendererEvent, args: { result: any }) => {
                resolve(args.result || [])
            })
        })
    }

    async getAudioStream(
        source: AudioSourceInfo,
        settings: AudioInputSettings
    ): Promise<{ track: AudioTrackWithInfo; modifiedSettings: Partial<AudioInputSettings> }> {
        return new Promise((resolve, reject) => {
            if (!source.audioEngineDeviceId) {
                reject(
                    new AudioEngineError(
                        'null_device_id',
                        `We're having trouble loading this device with Pro Audio support.`
                    )
                )
                return
            }

            if (typeof settings.sampleRate !== 'number' || settings.sampleRate <= 0) {
                settings.sampleRate = 44100
            }
            if (typeof settings.bitDepth !== 'number' || settings.bitDepth <= 0) {
                settings.bitDepth = 16
            }
            if (!Array.isArray(settings.channelSelection) || settings.channelSelection.length === 0) {
                settings.channelSelection = [[0]]
            }

            ipc.send('AudioEngine.getAudioStream', {
                audioEngineDeviceId: source.audioEngineDeviceId,
                settings: {
                    sampleRate: settings.sampleRate,
                    bitDepth: settings.bitDepth,
                    channelSelection: settings.channelSelection,
                    isCompressed: settings.isCompressed || false,
                    compressionBitrate: settings.compressionBitrate || 0,
                    networkPacketDurationMs: settings.networkPacketDurationMs || 0,
                    receiverBufferMaxDriftMs: settings.receiverBufferMaxDriftMs || 0,
                },
            })
            ipc.once(
                'AudioEngine.getAudioStream',
                (_: IpcRendererEvent, reply: { result: AudioEngineAudioStreamInfo } | { error: AudioEngineError }) => {
                    if ('error' in reply) {
                        reject(reply.error)
                        return
                    }

                    // recall that getAudioStream() will select default values for
                    // sampleRate, channelSelection, and bitDepth if it fails to access the device
                    // with the initial values.

                    const info = reply.result

                    const params: FakeMediaStreamTrackConstructorParams = {
                        id: info.streamUuid,
                        label: source.label,
                        channelSelection: info.channelSelection,
                        channelUnpackingMap: info.channelUnpackingMap,
                        localTrackStopHandler: () => {
                            ipc.send('AudioEngine.removeAudioStream', { streamUuid: info.streamUuid })
                        },
                        streamSettingsChangedHandler: ({ isMuted, volume }) => {
                            const data: any = { streamUuid: info.streamUuid }
                            if (isMuted != null) {
                                data.isMuted = isMuted
                            }
                            if (volume != null) {
                                data.volume = volume
                            }

                            ipc.send('AudioEngine.setAudioStreamSettings', data)
                        },
                    }
                    const track = new FakeMediaStreamTrack(params) as unknown as AudioTrackWithInfo
                    track.info = {
                        ...source,
                        ...settings,
                        audioEngineStreamUuid: info.streamUuid,
                        sampleRate: info.sampleRate,
                        bitDepth: info.bitDepth,
                        isCompressed: info.isCompressed,
                        compressionBitrate: info.compressionBitrate,
                        channelCount: info.channelCount,
                        channelSelection: info.channelSelection,
                        channelPackingMap: info.channelPackingMap,
                        channelUnpackingMap: info.channelUnpackingMap,
                    }
                    this.openTracks.set(info.streamUuid, track)

                    let modifiedSettings: Partial<AudioInputSettings> = {}
                    if (info.sampleRate !== settings.sampleRate) {
                        modifiedSettings.sampleRate = info.sampleRate
                    }
                    if (JSON.stringify(info.channelSelection) !== JSON.stringify(settings.channelSelection)) {
                        modifiedSettings.channelSelection = info.channelSelection
                    }
                    if (info.bitDepth !== settings.bitDepth) {
                        modifiedSettings.bitDepth = info.bitDepth
                    }

                    resolve({ track, modifiedSettings })
                }
            )
        })
    }

    startRemoteRecording(
        streamId: string,
        _channelGroup: number | null,
        outputFileFullPath: string | null,
        source: AudioSourceInfo | null,
        settings: AudioOutputSettings | null
    ): void {
        // open output device PA_REMOTE_RECORDING and copy this stream (this.id) to the device.
        if (source && settings) {
            const settings2 = {
                ...settings,
                isSelective: true,
                includeStreams: [streamId],
            }

            this.setAudioOutputDevice(source, settings2, AudioEngineOutputDeviceType.PA_REMOTE_RECORDING)
        }

        // open output device `AudioEngineOutputDeviceType.FILE_LOCAL_LOOPBACK`.
        if (outputFileFullPath) {
            const source2 = {
                audioEngineDeviceId: 'file://' + outputFileFullPath,
            }
            const settings2: AudioOutputSettings = {
                sampleRate: 48000,
                bitDepth: 16,
                channelCount: 2,
                compressionBitrate: 256000,
                isSelective: true,
                includeStreams: [streamId],
                excludeStreams: [],
            }
            /* @ts-ignore maybe fields on audio source info need to be marked as optional. */
            this.setAudioOutputDevice(source2, settings2, AudioEngineOutputDeviceType.FILE_REMOTE_LOOPBACK)
        }
    }

    stopRemoteRecording(_streamId: string, _channelGroup: number | null) {
        // NOTE: This currently stops all recording, not just the specified stream and channel group.
        this.setAudioOutputDevice(null, null, AudioEngineOutputDeviceType.PA_REMOTE_RECORDING)
        this.setAudioOutputDevice(null, null, AudioEngineOutputDeviceType.FILE_REMOTE_LOOPBACK)
    }

    startLocalRecording(streamId: string, filePath: string, _channelGroup?: number | undefined): void {
        // open output device `AudioEngineOutputDeviceType.FILE_LOCAL_LOOPBACK`.
        const source2 = {
            audioEngineDeviceId: 'file://' + filePath,
        }
        const settings2: AudioOutputSettings = {
            sampleRate: 48000,
            bitDepth: 16,
            channelCount: 2,
            compressionBitrate: 256000,
            isSelective: true,
            includeStreams: [streamId],
            excludeStreams: [],
        }

        const track = this.openTracks.get(streamId) || null
        if (!(track instanceof FakeMediaStreamTrack)) {
            return
        }

        /* @ts-ignore :/ */
        this.setAudioOutputDevice(source2, settings2, AudioEngineOutputDeviceType.FILE_LOCAL_LOOPBACK)

        // create local loopback
        this.setAudioLoopbackStream(track, AudioEngineOutputDeviceType.FILE_LOCAL_LOOPBACK)
    }

    stopLocalRecording(_streamId: string, _channelGroup?: number | undefined): void {
        // NOTE: Not currently possible to stop a specific recording. It's
        // assumed that the user is only recording one track at a time?
        this.setAudioOutputDevice(null, null, AudioEngineOutputDeviceType.FILE_LOCAL_LOOPBACK)
    }

    // NOTE: I would like to restructure this API so that setting the primary
    // output device is separated from looping specific streams to virtual
    // devices for recording.
    async setAudioOutputDevice(
        source: AudioSourceInfo | null,
        settings: AudioOutputSettings | null,
        outputIdx: AudioEngineOutputDeviceType = 0
    ): Promise<boolean> {
        return new Promise((resolve, reject) => {
            // backward compatibility
            if (settings != null) {
                settings = audioOutputSettingsCompatibility(source, settings)
            }

            if (source && !source.audioEngineDeviceId) {
                log.error('setAudioOutputDevice', 'source.audioEngineDeviceId is null!')
                reject({
                    code: 'null_device_id',
                    debugMessage: 'Failed to access the Pro Audio output device because the device ID is null.',
                })
                return
            }

            if (env.TEST_AUDIO_ENGINE_CRASH_ON_SET_AUDIO_OUTPUT_DEVICE) {
                if (settings) {
                    // @ts-ignore
                    settings.channelCount = null
                    env.TEST_AUDIO_ENGINE_CRASH_ON_SET_AUDIO_OUTPUT_DEVICE = null
                }
            }

            ipc.send('AudioEngine.setAudioOutputDevice', {
                audioEngineDeviceId: source?.audioEngineDeviceId || null,
                settings: settings,
                outputIdx,
            })

            ipc.once(
                'AudioEngine.setAudioOutputDevice',
                (_ev: IpcRendererEvent, result: { result: string } | { error: AudioEngineError }) => {
                    if ('error' in result) {
                        reject(result.error)
                        return
                    }

                    resolve(true)
                }
            )
        })
    }

    async setAudioLoopbackStream(
        track: AudioTrackWithInfo | FakeMediaStreamTrack,
        outputIdx: number = 0
    ): Promise<boolean> {
        return new Promise((resolve, reject) => {
            ipc.send('AudioEngine.setAudioLoopbackStream', {
                streamUuid: track.id,
                outputIdx: outputIdx,
            })

            ipc.once(
                'AudioEngine.setAudioLoopbackStream',
                (_ev: IpcRendererEvent, result: { result: string } | { error: AudioEngineError }) => {
                    if ('error' in result) {
                        reject(result.error)
                        return
                    }

                    resolve(true)
                }
            )
        })
    }

    async unsetAudioLoopbackStream(track: AudioTrackWithInfo): Promise<boolean> {
        return new Promise((resolve, reject) => {
            ipc.send('AudioEngine.unsetAudioLoopbackStream', {
                streamUuid: track.id,
            })

            ipc.once(
                'AudioEngine.unsetAudioLoopbackStream',
                (_ev: IpcRendererEvent, result: { result: string } | { error: AudioEngineError }) => {
                    if ('error' in result) {
                        reject(result.error)
                        return
                    }

                    resolve(true)
                }
            )
        })
    }

    async startMasterCaptureToFile(filePath: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            // hard-coded settings for now
            const settings: AudioOutputSettings = {
                sampleRate: 48000,
                bitDepth: 16,
                channelCount: 2,
                compressionBitrate: 256000,
                isSelective: false,
                includeStreams: [],
                excludeStreams: [],
            }

            ipc.send('AudioEngine.setAudioOutputDevice', {
                audioEngineDeviceId: 'file://' + filePath, // e.g. file://D:\\User\\test.mp3',
                settings: settings,
                outputIdx: AudioEngineOutputDeviceType.FILE_MASTER_CAPTURE,
            })

            ipc.once(
                'AudioEngine.setAudioOutputDevice',
                (_ev: IpcRendererEvent, result: { result: string } | { error: AudioEngineError }) => {
                    if ('error' in result) {
                        reject(result.error)
                        return
                    }

                    resolve(true)
                }
            )
        })
    }

    async stopMasterCaptureToFile(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            ipc.send('AudioEngine.setAudioOutputDevice', {
                audioEngineDeviceId: '',
                outputIdx: AudioEngineOutputDeviceType.FILE_MASTER_CAPTURE,
            })

            ipc.once(
                'AudioEngine.setAudioOutputDevice',
                (_ev: IpcRendererEvent, result: { result: string } | { error: AudioEngineError }) => {
                    if ('error' in result) {
                        reject(result.error)
                        return
                    }

                    resolve(true)
                }
            )
        })
    }

    quit() {
        ipc.send('AudioEngine.quit', {})
    }

    async testCrash(): Promise<boolean> {
        return new Promise(() => {
            ipc.send('AudioEngine.testCrash', {})
        })
    }

    get implementation(): AudioEngineImpl {
        return 'native'
    }
}
